import React, { useState } from 'react';
import {
  X,
  Server,
  Users,
  GitBranch,
  Shield,
  Activity,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Zap,
  Share2,
  Clock,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';

const TYPE_ICONS = {
  SERVICE: Server,
  TEAM: Users,
  PATTERN: GitBranch,
  API: Activity,
  REGULATION: Shield,
};

const TYPE_COLORS = {
  SERVICE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  TEAM: 'bg-green-500/20 text-green-400 border-green-500/30',
  PATTERN: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  API: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  REGULATION: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const REL_COLORS = {
  DEPENDS_ON: 'text-gray-400',
  OWNED_BY: 'text-green-400',
  USES_PATTERN: 'text-purple-400',
  COMPLIES_WITH: 'text-red-400',
  INTEGRATES_WITH: 'text-blue-400',
};

const REL_NAMES = {
  DEPENDS_ON: 'Depends On',
  OWNED_BY: 'Owned By',
  USES_PATTERN: 'Uses Pattern',
  COMPLIES_WITH: 'Complies With',
  INTEGRATES_WITH: 'Integrates With',
  CALLS: 'Calls',
  PART_OF: 'Part Of',
  DOCUMENTED_IN: 'Documented In',
  MAINTAINS: 'Maintains',
};

const CRITICALITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30',
};

function EntityDetails({ entity, relationships, onClose, onSelectEntity, allEntities }) {
  const [expandedRelationships, setExpandedRelationships] = useState({});

  if (!entity) return null;

  const Icon = TYPE_ICONS[entity.entity_type] || Server;
  const colorClass = TYPE_COLORS[entity.entity_type] || TYPE_COLORS.SERVICE;

  const getEntityName = (id) => {
    const e = allEntities.find(ent => ent.id === id);
    return e ? e.name : id;
  };

  const getEntityType = (id) => {
    const e = allEntities.find(ent => ent.id === id);
    return e ? e.entity_type : 'UNKNOWN';
  };

  const getEntityColor = (type) => {
    return TYPE_COLORS[type] || TYPE_COLORS.SERVICE;
  };

  const toggleRelationshipExpanded = (key) => {
    setExpandedRelationships(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculate entity statistics
  const stats = {
    outgoingCount: relationships.outgoing.length,
    incomingCount: relationships.incoming.length,
    totalConnections: relationships.outgoing.length + relationships.incoming.length,
    types: {}
  };

  relationships.outgoing.forEach(rel => {
    stats.types[rel.rel_type] = (stats.types[rel.rel_type] || 0) + 1;
  });

  // Get criticality level if it exists
  const criticality = entity.metadata?.criticality;
  const criticialityColor = criticality ? CRITICALITY_COLORS[criticality] : null;

  return (
    <div className="h-full flex flex-col bg-dark-800 border-l border-dark-600">
      {/* Header */}
      <div className="p-4 border-b border-dark-600">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${colorClass}`}>
            <Icon size={24} />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-lg">{entity.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2 py-0.5 rounded font-mono ${colorClass}`}>
                {entity.entity_type}
              </span>
              {criticality && (
                <span className={`text-xs px-2 py-0.5 rounded capitalize ${criticialityColor}`}>
                  {criticality}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-dark-600">
          <div className="text-center">
            <div className="text-lg font-semibold text-accent-purple">{stats.outgoingCount}</div>
            <div className="text-xs text-dark-400">Outgoing</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-accent-purple">{stats.incomingCount}</div>
            <div className="text-xs text-dark-400">Incoming</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-accent-purple">{stats.totalConnections}</div>
            <div className="text-xs text-dark-400">Total Links</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Description */}
        <div>
          <h3 className="text-sm font-semibold text-dark-300 mb-2 flex items-center gap-2">
            <AlertCircle size={14} />
            About
          </h3>
          <p className="text-sm leading-relaxed text-dark-200">
            {entity.description || 'No description available'}
          </p>
        </div>

        {/* Metadata/Attributes */}
        {entity.metadata && Object.keys(entity.metadata).length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-dark-300 mb-3">Properties</h3>
            <div className="space-y-2">
              {Object.entries(entity.metadata).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-dark-700/50 rounded-lg border border-dark-600/50">
                  <span className="text-dark-400 capitalize text-sm">{key.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-sm text-dark-100 font-medium">{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Outgoing Relationships */}
        {relationships.outgoing.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-dark-300 mb-3 flex items-center gap-2">
              <ArrowRight size={14} />
              Outgoing Dependencies ({relationships.outgoing.length})
            </h3>
            <div className="space-y-2">
              {relationships.outgoing.map((rel, i) => {
                const isExpanded = expandedRelationships[`out-${i}`];
                const targetEntity = allEntities.find(e => e.id === rel.target_id);
                return (
                  <div key={i} className="bg-dark-700/50 border border-dark-600/50 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleRelationshipExpanded(`out-${i}`)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-dark-600/30 transition-colors text-left"
                    >
                      <span className={`text-xs font-mono px-2 py-1 rounded bg-dark-600 ${REL_COLORS[rel.rel_type] || 'text-gray-400'}`}>
                        {REL_NAMES[rel.rel_type] || rel.rel_type}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{getEntityName(rel.target_id)}</p>
                        <p className="text-xs text-dark-400">{getEntityType(rel.target_id)}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectEntity(rel.target_id);
                        }}
                        className="p-1 hover:bg-dark-500 rounded transition-colors ml-2"
                        title="Navigate to entity"
                      >
                        <Zap size={14} className="text-accent-purple" />
                      </button>
                      {rel.description && (
                        isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </button>
                    {isExpanded && rel.description && (
                      <div className="px-3 py-2 border-t border-dark-600/50 bg-dark-800/50">
                        <p className="text-xs text-dark-300">{rel.description}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Incoming Relationships */}
        {relationships.incoming.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-dark-300 mb-3 flex items-center gap-2">
              <ArrowLeft size={14} />
              Incoming Dependencies ({relationships.incoming.length})
            </h3>
            <div className="space-y-2">
              {relationships.incoming.map((rel, i) => {
                const isExpanded = expandedRelationships[`in-${i}`];
                return (
                  <div key={i} className="bg-dark-700/50 border border-dark-600/50 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleRelationshipExpanded(`in-${i}`)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-dark-600/30 transition-colors text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{getEntityName(rel.source_id)}</p>
                        <p className="text-xs text-dark-400">{getEntityType(rel.source_id)}</p>
                      </div>
                      <span className={`text-xs font-mono px-2 py-1 rounded bg-dark-600 ${REL_COLORS[rel.rel_type] || 'text-gray-400'}`}>
                        {REL_NAMES[rel.rel_type] || rel.rel_type}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectEntity(rel.source_id);
                        }}
                        className="p-1 hover:bg-dark-500 rounded transition-colors ml-2"
                        title="Navigate to entity"
                      >
                        <Zap size={14} className="text-accent-purple" />
                      </button>
                      {rel.description && (
                        isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </button>
                    {isExpanded && rel.description && (
                      <div className="px-3 py-2 border-t border-dark-600/50 bg-dark-800/50">
                        <p className="text-xs text-dark-300">{rel.description}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Source */}
        {entity.source_url && (
          <div>
            <h3 className="text-sm font-semibold text-dark-300 mb-2 flex items-center gap-2">
              <Clock size={14} />
              Source
            </h3>
            <a
              href={entity.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-accent-purple hover:text-accent-purple-light transition-colors"
            >
              <ExternalLink size={14} />
              View Original Document
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntityDetails;
